import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { filter, finalize, map, Observable, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  AUTH_MAIN_ROUTE,
  AUTH_ROUTES,
} from '../../../constants/auth.routing.constants';
import { FEATURE_FLAGS } from '../../../constants/feature-flags.constants';
import { RoleTypes } from '../../../constants/role-types.constant';
import { AppStorage } from '../../../enums/app-storage';
import { LogoutHelperService } from '../../../helpers/logout-helper/logout-helper.service';
import { isVendorPortalUrl } from '../../../helpers/portal/is-vendor-portal-url';
import { LAST_WORKSPACE_COOKIE_DAYS } from '../../../helpers/workspace/workspace.helper';
import { ApiResponse } from '../../../models/api-response.model';
import { Language } from '../../../models/language.model';
import { Workspace } from '../../../models/workspace.model';
import { AuthService } from '../../../services/auth/auth.service';
import { CookieService } from '../../../services/cookie/cookie.service';
import { FeatureFlagsService } from '../../../services/feature-flags/feature-flags.service';
import { StatsigUserService } from '../../../services/feature-flags/statsig-user.service';
import { LanguageChangeService } from '../../../services/language-change/language-change.service';
import { RoleHelperService } from '../../../services/role-helper/role-helper.service';
import { TenantService } from '../../../services/tenant/tenant.service';

export type Portal = 'employee' | 'admin' | 'vendor';

/** Profile-menu actions shared by the old profile dropdown and the KUIP-68 side-menu profile. */
@Injectable({ providedIn: 'root' })
export class ProfileActionsService {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly tenantService = inject(TenantService);
  private readonly cookieService = inject(CookieService);
  private readonly statsigUserService = inject(StatsigUserService);
  private readonly roleHelper = inject(RoleHelperService);
  private readonly titleService = inject(Title);
  private readonly languageChangeService = inject(LanguageChangeService);
  private readonly logoutHelper = inject(LogoutHelperService);
  private readonly featureFlagsService = inject(FeatureFlagsService);

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map(event => event.urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  readonly currentPortal = computed<Portal>(() => {
    const url = this.url() ?? '';
    if (isVendorPortalUrl(url)) {
      return 'vendor';
    }
    return url.startsWith(this.tenantService.adminPath())
      ? 'admin'
      : 'employee';
  });

  readonly reachablePortals = computed<Portal[]>(() => {
    const roles: string[] =
      this.authService.selectedWorkspace()?.user_roles ?? [];
    const portals: Portal[] = [];
    // Anyone with a non-vendor role uses the employee app (admins included).
    if (roles.some(role => role !== RoleTypes.VENDOR)) {
      portals.push('employee');
    }
    if (
      roles.includes(RoleTypes.ADMIN) ||
      roles.includes(RoleTypes.SUPER_ADMIN)
    ) {
      portals.push('admin');
    }
    if (
      roles.includes(RoleTypes.VENDOR) &&
      this.featureFlagsService.getFeatureFlagValue(
        FEATURE_FLAGS.SYSTEM.portal_settings
      )
    ) {
      portals.push('vendor');
    }
    return portals;
  });

  /** Refetches the tenant list (not persisted) and keeps the selected workspace in sync. */
  loadWorkspaces(): Observable<Workspace[]> {
    return this.authService.centralWorkspace().pipe(
      map((response: ApiResponse<Workspace>) => response.data ?? []),
      tap(workspaces => {
        this.authService.workspaces.set(workspaces);
        const selected = workspaces.find(
          workspace =>
            workspace.slug === this.authService.selectedWorkspace()?.slug
        );
        if (selected) {
          this.authService.selectedWorkspace.set(selected);
        }
      })
    );
  }

  switchWorkspace(slug: string): void {
    const workspace = (this.authService.workspaces() || []).find(
      w => w.slug === slug
    );
    if (!workspace) {
      return;
    }
    this.cookieService.set(
      AppStorage.selectedWorkspace,
      JSON.stringify(workspace)
    );
    this.cookieService.set(AppStorage.tenant, workspace.slug);
    // remember the workspace so the next login can return to it
    this.cookieService.set(AppStorage.lastWorkspace, workspace.slug, {
      expires: LAST_WORKSPACE_COOKIE_DAYS,
    });
    this.statsigUserService.updateTenant(workspace.slug);
    this.roleHelper.handleRole(workspace.user_roles || [], '', '', workspace);
    this.authService.selectedWorkspace.set(workspace);
    this.titleService.setTitle(workspace.name ?? workspace.slug);
  }

  /** Workspaces are created on the central domain; the session cookie is shared across subdomains. */
  createWorkspace(): void {
    location.href = `${location.protocol}//${environment.domain}/${AUTH_MAIN_ROUTE}/${AUTH_ROUTES.CREATE_WORKSPACE}`;
  }

  switchPortal(target: Portal): void {
    if (target === 'admin') {
      this.router.navigate([this.tenantService.adminPath()]);
      return;
    }
    if (target === 'employee') {
      this.router.navigate([this.tenantService.tenantPath('')]);
      return;
    }
    const workspace = this.authService.selectedWorkspace();
    if (workspace) {
      this.roleHelper.handleNavigationBasedOnRole(
        [RoleTypes.VENDOR],
        '',
        '',
        workspace
      );
    }
  }

  changeLanguage(lang: Language): void {
    this.languageChangeService.changeLanguage(lang);
  }

  logout(): void {
    this.authService
      .logout()
      .pipe(finalize(() => this.logoutHelper.handleLogout()))
      .subscribe();
  }
}
